import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';

/**
 * ADR-018 §4 — leitura de app_permissions do usuário corrente.
 *
 * Ajuste 1 do mini-Council Gemini: NUNCA dispara fetch enquanto auth ainda
 * estiver carregando ou user.id ausente — evitar query com user_id=undefined
 * que retorna 0 rows e simula "sem permissão".
 *
 * Retorno: { permissions, loading, error, refetch }
 *  - permissions: string[] — ex. ['lands.read','launches.read','intelligence.read']
 *  - loading: true até auth resolver E permissions chegarem
 */
export function useAppPermissions() {
  const { user, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user?.id) {
      setPermissions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('app_permissions')
        .select('permission')
        .eq('user_id', user.id);
      if (err) throw err;
      setPermissions((data || []).map((r) => r.permission));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [user?.id, authLoading]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  return {
    permissions,
    loading: loading || authLoading,
    error,
    refetch: fetchPermissions,
  };
}
